"use client";

import { AlertCircle, Check, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Text } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/core/i18n";
type CaptureValidationActionsProps = {
  canAdvance: boolean;
  isBlocked: boolean;
  onRetake: () => void;
  onAdvance: () => void;
  /**
   * Optional human-readable error describing why the user is blocked
   * (e.g. "Upload failed. Please try again."). When provided it is rendered
   * above the recovery buttons so the user understands what went wrong — the
   * previous behavior silently surfaced a Retake button with no explanation.
   */
  errorMessage?: string;
  /**
   * Optional async callback that retries the failed operation without
   * discarding the captured photo. When provided (typical for upload
   * failures), the overlay renders "Retry upload" as the primary recovery and
   * demotes "Retake photo" to a secondary option — most transient failures
   * are network blips, and forcing a full retake here is wasteful.
   */
  onRetryUpload?: () => Promise<unknown> | void;
};
export function CaptureValidationActions({
  canAdvance,
  isBlocked,
  onRetake,
  onAdvance,
  errorMessage,
  onRetryUpload
}: CaptureValidationActionsProps): React.ReactElement | null {
  const {
    t
  } = useTranslation();
  const [isRetryingUpload, setIsRetryingUpload] = useState(false);
  if (!canAdvance && !isBlocked) return null;
  if (isBlocked) {
    const handleRetryUpload = async () => {
      if (!onRetryUpload) return;
      setIsRetryingUpload(true);
      try {
        await Promise.resolve(onRetryUpload());
      } catch {
        // Consumer's onRetryUpload is expected to handle its own errors (it
        // typically sets an `uploadError` state, which is what we render
        // above). We still swallow here so an unexpected throw doesn't leave
        // the button stuck in a permanent "Retrying…" state.
      } finally {
        setIsRetryingUpload(false);
      }
    };
    return <div className="flex w-full max-w-xs flex-col gap-3">
        {errorMessage && <div className="flex items-start gap-2 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-left text-red-300" data-testid="capture-validation-error-message" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <Text component="p" text={errorMessage} tone="inherit" variant="body-sm" />
          </div>}

        {onRetryUpload && <Button aria-busy={isRetryingUpload} className="text-white" data-testid="capture-validation-retry-upload" disabled={isRetryingUpload} type="button" onClick={handleRetryUpload}>
            {isRetryingUpload ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            <Text component="span" text={t(isRetryingUpload ? "assessmentSteps.studioSession.preview.retryingUpload" : "assessmentSteps.studioSession.preview.retryUpload")} />
          </Button>}

        <Button className="text-white" data-testid="capture-validation-retake" disabled={isRetryingUpload} type="button" variant={onRetryUpload ? "ghost" : "outline"} onClick={onRetake}>
          <RefreshCw className="mr-2 h-4 w-4" />
          <Text component="span" text={t("assessmentSteps.studioSession.preview.retakePhoto")} />
        </Button>
      </div>;
  }
  return <div className="flex w-full max-w-xs flex-col gap-3">
      <Button className="text-white" type="button" onClick={onAdvance}>
        <Check className="mr-2 h-4 w-4" />
        <Text component="span" text={t("assessmentSteps.studioSession.preview.continue")} />
      </Button>

      <Button className="text-white" type="button" variant="ghost" onClick={onRetake}>
        <RefreshCw className="mr-2 h-4 w-4" />
        <Text component="span" text={t("assessmentSteps.studioSession.preview.retake")} />
      </Button>
    </div>;
}